import { CSSProperties, useCallback, useEffect, useRef } from 'react';
import { Box, Paper, Typography } from '@mui/material';

import { primitiveColors, BORDER_RADIUS } from '@/theme/tokens';

import { ChatMessage } from '../ChatComponents';

interface UserMessageProps {
  message: ChatMessage;
  sx?: CSSProperties;
}

const BUBBLE_PADDING_X = 16; // MUI `p: 2`

export const UserMessage: React.FC<UserMessageProps> = ({ message, sx }) => {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);

  // A wrapped message otherwise keeps the full responsive cap width, leaving
  // dead space beside its shortest line. Measuring the laid-out line boxes
  // lets the bubble hug the widest one, the way ChatGPT and Claude do.
  const fitToWidestLine = useCallback(() => {
    const bubble = bubbleRef.current;
    const text = textRef.current;
    if (!bubble || !text) return;

    bubble.style.width = '';
    const range = document.createRange();
    range.selectNodeContents(text);
    const widest = Array.from(range.getClientRects()).reduce(
      (max, rect) => Math.max(max, rect.width),
      0
    );
    if (widest > 0) {
      bubble.style.width = `${Math.ceil(widest) + BUBBLE_PADDING_X * 2}px`;
    }
  }, []);

  useEffect(() => {
    fitToWidestLine();

    // Every bubble observes the same container, so a drag-resize would force a
    // reflow per bubble per event. One measurement per frame is enough.
    let frame: number | null = null;
    const scheduleFit = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        fitToWidestLine();
      });
    };

    const container = bubbleRef.current?.parentElement;
    const observer =
      container && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleFit)
        : undefined;
    observer?.observe(container as Element);

    // Web fonts land after first paint and change the measurement.
    void document.fonts?.ready.then(scheduleFit).catch(() => {});

    return () => {
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [message.text, fitToWidestLine]);

  return (
    <Box display="flex" mb={2} justifyContent="flex-end">
      <Paper
        ref={bubbleRef}
        elevation={0}
        sx={{
          p: 2,
          background: primitiveColors.white,
          borderRadius: BORDER_RADIUS,
          // Size to content up to the responsive cap — short messages hug
          // the text, long messages wrap at the cap like Claude/ChatGPT.
          display: 'inline-block',
          maxWidth: { xs: '85%', sm: '80%', md: '75%', lg: '70%' },
        }}
      >
        <Typography
          ref={textRef}
          component="p"
          color={primitiveColors.rockSlate}
          sx={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            ...sx,
          }}
        >
          {message.text}
        </Typography>
      </Paper>
    </Box>
  );
};
